/**
 * Fenêtre superposée à la carte affichant une page externe dans une `<iframe>` (voir
 * `Shape.urlAction.openMode === "overlay"`, `createTokaMap.ts`'s `shape:click`) — permet de
 * consulter un lien sans jamais quitter la carte, contrairement à `window.open`. Dépend du site
 * cible : un site qui refuse l'embarquement (`X-Frame-Options`/`Content-Security-Policy:
 * frame-ancestors`) affichera une iframe vide, sans qu'on puisse le détecter côté client (le
 * navigateur ne remonte aucune erreur au script pour une iframe cross-origin bloquée). Même
 * patron que `ui/EntityPanel`/`ui/ContextMenu` : un seul `element` vivant à la fois, `show()`
 * ferme le précédent avant d'ouvrir le nouveau.
 */
export class UrlOverlay {
  private element: HTMLDivElement | null = null;

  constructor(private container: HTMLElement) {}

  show(url: string): void {
    this.close();

    const backdrop = document.createElement("div");
    backdrop.className = "toka-url-overlay";
    // Clic sur le fond assombri (pas sur la fenêtre elle-même) : ferme, comme un modal classique.
    backdrop.addEventListener("click", (event) => {
      if (event.target === backdrop) this.close();
    });

    const frame = document.createElement("div");
    frame.className = "toka-url-overlay__frame";

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "toka-url-overlay__close";
    closeButton.textContent = "×";
    closeButton.setAttribute("aria-label", "Fermer");
    closeButton.addEventListener("click", () => this.close());
    frame.appendChild(closeButton);

    const iframe = document.createElement("iframe");
    iframe.className = "toka-url-overlay__iframe";
    iframe.src = url;
    frame.appendChild(iframe);

    backdrop.appendChild(frame);
    this.container.appendChild(backdrop);
    this.element = backdrop;

    document.addEventListener("keydown", this.handleKeyDown);
  }

  close(): void {
    if (!this.element) return;
    document.removeEventListener("keydown", this.handleKeyDown);
    this.element.remove();
    this.element = null;
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") this.close();
  };
}
